<script lang="ts">
interface Props {
    customerCompanyId?: uuid
}
</script>
<script lang="ts" setup>
import { useI18n } from 'vue-i18n'
import axios from 'axios'
import { computed, ref } from 'vue'
import { RouteLocationRaw, useRoute } from 'vue-router'

import { uuid } from '@/types/general'
import { emitter } from '@/utils/mitt'
import { useAuthStore } from '@/stores/auth-store'
import { MinimalShipment } from '@/types/delivery-management'
import { useConfirm } from '@/hooks/use-confirm'
import { MyButtonScheme } from '@/types/layout/my-button'
import { useFileDownloader } from '@/hooks/use-file-downloader'

import ActionMenuItem from '@/components/table/ActionMenuItem.vue'
import ManageConsignmentNoteModal from '@/components/e-cmr/ManageConsignmentNoteModal.vue'
import ShipmentsTable from '@/components/delivery-management/ShipmentsTable.vue'
import ActionRowItem from '@/components/table/ActionRowItem.vue'
import ConsignmentNoteModal from '@/components/e-cmr/ConsignmentNoteModal.vue'
import BasicShareModal from '@/components/BasicShareModal.vue'

const props = defineProps<Props>()
const { t } = useI18n()
const authStore = useAuthStore()
const route = useRoute()
const confirm = useConfirm()
const consignmentNoteModalOpen = ref(false)
const cmrModalOpen = ref(false)
const shareConsignmentNoteOpen = ref(false)
const selectedShipment = ref<MinimalShipment>()
const cmrModalAction = ref<'edit' | 'duplicate'>('edit')

const { downloading, downloadFile } = useFileDownloader()

const shareEndpoint = computed(() => {
    if (!authStore.companyId || !selectedShipment.value?.id) return ''

    return window.route('ecmr.company.consignment-notes.share', {
        company: authStore.companyId,
        shipment: selectedShipment.value.id,
    })
})

async function handleDownload(shipment: MinimalShipment) {
    const url = window.route('ecmr.consignment-notes.download', {
        shipment: shipment.id,
    })

    await downloadFile(url)
}

async function deleteConsignmentNote(shipment: MinimalShipment) {
    try {
        await confirm(
            t('deleteEntityTitle', { entity: t('consignmentNote') }),
            t('deleteEntityDescription', { entity: t('consignmentNote') }),
            {
                confirmText: t('yes'),
                cancelText: t('no'),
                confirmButtonScheme: MyButtonScheme.Warning,
            },
        )
    } catch {
        return
    }
    await axios.delete(
        window.route('ecmr.company.consignment-notes.destroy', {
            company: authStore.companyId,
            consignment_note: shipment.consignmentNote?.id,
        }),
    )
    emitFetchShipmentsEvent()
}
function isSenderOrShipper(shipment: MinimalShipment) {
    if (shipment.sendingCompany.id === authStore.companyId) return true
    if (shipment.shipperCompany?.id === authStore.companyId) return true
    return false
}
function duplicateConsignmentNote(shipment: MinimalShipment) {
    selectedShipment.value = shipment
    cmrModalAction.value = 'duplicate'
    consignmentNoteModalOpen.value = true // use the correct modal
}
function updateConsignmentNote(shipment: MinimalShipment) {
    selectedShipment.value = shipment
    consignmentNoteModalOpen.value = true
}
function shareConsignmentNote(shipment: MinimalShipment) {
    selectedShipment.value = shipment
    shareConsignmentNoteOpen.value = true
}
async function consignmentNoteModalClosed() {
    consignmentNoteModalOpen.value = false
    selectedShipment.value = undefined
    cmrModalAction.value = 'edit'
}
async function cmrModalClosed() {
    cmrModalOpen.value = false
    selectedShipment.value = undefined
}
function emitFetchShipmentsEvent() {
    emitter?.emit('fetchShipments')
}
function showConsignmentNote(id: string): RouteLocationRaw {
    if (route.name?.toString()?.includes('customers.show')) {
        return {
            name: 'ecmr.customers.consignment-notes.show',
            params: { ...route.params, shipmentId: id },
            query: route.query,
        }
    }
    return {
        name: 'ecmr.consignment-notes.show',
        params: { shipmentId: id },
        query: route.query,
    }
}
</script>

<template>
    <ShipmentsTable
        table-id="ecmr-shipments"
        :entity-name="'consignmentNote'"
        :fetch-endpoint="`ecmr.company.consignment-notes.index`"
        :customer-company-id="props.customerCompanyId"
        :downloading="downloading"
    >
        <template #customActionRow="{ row }">
            <RouterLink :to="showConsignmentNote(row.id)">
                <ActionRowItem>
                    <mdi:eye />
                </ActionRowItem>
            </RouterLink>
        </template>

        <template #customActionMenu="{ row }">
            <template v-if="isSenderOrShipper(row)">
                <ActionMenuItem @click="updateConsignmentNote(row)">
                    <span class="mr-2"><mdi:pencil /></span>
                    {{ t('edit') }}
                </ActionMenuItem>
                <ActionMenuItem @click="duplicateConsignmentNote(row)">
                    <span class="mr-2"><mdi:content-copy /></span>
                    {{ t('duplicate') }}
                </ActionMenuItem>
                <ActionMenuItem @click="handleDownload(row)">
                    <span class="mr-2"><mdi:download /></span>
                    {{ t('downloadPdf') }}
                </ActionMenuItem>
                <ActionMenuItem @click="shareConsignmentNote(row)">
                    <mdi:share class="mr-2" />
                    {{ t('shareEntity', { entity: t('consignmentNote') }) }}
                </ActionMenuItem>
                <ActionMenuItem
                    v-if="row.sendingCompany.id === authStore.companyId"
                    @click="deleteConsignmentNote(row)"
                >
                    <span class="mr-2"><mdi:trash-can /></span>
                    {{ t('remove') }}
                </ActionMenuItem>
            </template>
        </template>
    </ShipmentsTable>

    <ManageConsignmentNoteModal
        v-model="consignmentNoteModalOpen"
        :shipment-id="selectedShipment?.id"
        :action="cmrModalAction"
        @close="consignmentNoteModalClosed"
        @saved="emitFetchShipmentsEvent"
    />

    <ConsignmentNoteModal
        v-if="selectedShipment"
        :id="selectedShipment.id"
        v-model:open="cmrModalOpen"
        @close="cmrModalClosed"
    />

    <BasicShareModal
        v-if="selectedShipment"
        v-model="shareConsignmentNoteOpen"
        :customer-company-id="selectedShipment.receivingCompany?.id"
        :entity="t('consignmentNote')"
        :endpoint="shareEndpoint"
    />
</template>
